import React, { useEffect, useState } from "react";
import { Siren } from "lucide-react";
import { api } from "../lib/api";

/**
 * Rotating red announcement strip that sits ABOVE the navbar.
 *
 * Sources from GET /api/announcements/active, which returns:
 *   - manual admin-authored entries (always first)
 *   - published urgent rescue alerts with is_strip_active=true
 *     (auto-filtered when status is "All Rescued" or "Closed")
 *
 * When multiple entries exist, they rotate every 7s. The whole strip is
 * clickable (anchors to link_url). Works on desktop and mobile.
 */
export default function AnnouncementStrip() {
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);

  // Hard-dismiss (per session) — admins can toggle via the manual control,
  // but once a visitor dismisses the strip we don't nag during their visit.
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("por_strip_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/announcements/active");
        if (!active) return;
        setEntries(Array.isArray(data?.entries) ? data.entries : []);
      } catch {
        if (!active) return;
        setEntries([]);
      } finally {
        if (active) setLoaded(true);
      }
    };
    load();
    const poll = setInterval(load, 60_000); // re-poll every minute
    return () => { active = false; clearInterval(poll); };
  }, []);

  useEffect(() => {
    if (entries.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % entries.length), 7000);
    return () => clearInterval(t);
  }, [entries.length]);

  if (!loaded || dismissed || entries.length === 0) return null;
  const entry = entries[idx % entries.length];
  const toneClass = {
    red: "bg-red-600 text-white",
    amber: "bg-amber-500 text-zinc-900",
    neutral: "bg-zinc-900 text-white",
  }[entry.tone || "red"];

  const content = (
    <div className="flex items-center justify-between gap-4 w-full">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Siren size={16} className="shrink-0 animate-pulse" aria-hidden/>
        <span className="truncate text-xs sm:text-sm font-bold uppercase tracking-wider">
          {entry.message}
        </span>
        {entry.unverified && (
          <span className="hidden sm:inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-black/30 ml-2">
            Unconfirmed report
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {entries.length > 1 && (
          <span className="hidden sm:inline text-[10px] font-mono opacity-70">
            {idx + 1}/{entries.length}
          </span>
        )}
        <button
          type="button"
          aria-label="Dismiss alert"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            try { sessionStorage.setItem("por_strip_dismissed", "1"); } catch {}
            setDismissed(true);
          }}
          className="text-xs opacity-70 hover:opacity-100 px-2 py-0.5"
          data-testid="announcement-strip-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );

  if (entry.link_url) {
    return (
      <a
        href={entry.link_url}
        className={`block ${toneClass} px-3 sm:px-5 py-2 border-b border-black/40 hover:brightness-110`}
        data-testid="announcement-strip"
      >
        {content}
      </a>
    );
  }
  return (
    <div className={`${toneClass} px-3 sm:px-5 py-2 border-b border-black/40`} data-testid="announcement-strip">
      {content}
    </div>
  );
}
